import {
    Box,
    IconButton,
    Stack,
    Typography,
    Button,
} from '@compass/core';
import { Menu, MenuItem, ListItemIcon, ListItemText, TextField, Chip as MuiChip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditSharpIcon from '@mui/icons-material/EditSharp';
import DeleteOutlineSharpIcon from '@mui/icons-material/DeleteOutlineSharp';
import ColorLensSharpIcon from '@mui/icons-material/ColorLensSharp';
import ArrowDropDownCircleSharpIcon from '@mui/icons-material/ArrowDropDownCircleSharp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { useState } from 'react';
import {
    DashboardTabConfig,
    TabId,
    TabColorKey,
    MAX_TABS,
    TAB_PALETTE_KEYS,
} from '../types/DashboardTabConfig';

interface DrawerTabsViewProps {
    tabs: DashboardTabConfig[];
    activeTabId: TabId;
    onAddTab: (label: string) => void;
    onRenameTab: (tabId: TabId, newLabel: string) => void;
    onDeleteTab: (tabId: TabId) => void;
    onSetTabColor: (tabId: TabId, colorKey: TabColorKey) => void;
}

export const DrawerTabsView = ({
    tabs,
    activeTabId,
    onAddTab,
    onRenameTab,
    onDeleteTab,
    onSetTabColor,
}: DrawerTabsViewProps) => {
    const theme = useTheme();
    const [editingTabId, setEditingTabId] = useState<TabId | null>(null);
    const [editingLabel, setEditingLabel] = useState('');
    const [newTabLabel, setNewTabLabel] = useState('');
    const [isAddingTab, setIsAddingTab] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [menuTabId, setMenuTabId] = useState<TabId | null>(null);
    const [colorPickerTabId, setColorPickerTabId] = useState<TabId | null>(null);

    const isAtTabLimit = tabs.length >= MAX_TABS;

    // ─── Menu Handlers

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, tabId: TabId) => {
        setMenuAnchorEl(event.currentTarget);
        setMenuTabId(tabId);
    };

    const handleCloseMenu = () => {
        setMenuAnchorEl(null);
        setMenuTabId(null);
    };

    // Tab Handlers

    const handleStartAddTab = () => {
        setNewTabLabel('');
        setIsAddingTab(true);
    };

    const handleConfirmAddTab = () => {
        const trimmed = newTabLabel.trim();
        if (!trimmed) return;
        onAddTab(trimmed);
        setNewTabLabel('');
        setIsAddingTab(false);
    };

    const handleCancelAddTab = () => {
        setNewTabLabel('');
        setIsAddingTab(false);
    };

    //Rename Handlers

    const handleStartRename = (tab: DashboardTabConfig) => {
        handleCloseMenu();
        setEditingTabId(tab.id);
        setEditingLabel(tab.label);
    };

    const handleConfirmRename = () => {
        if (!editingTabId) return;
        const trimmed = editingLabel.trim();
        if (!trimmed) return;
        onRenameTab(editingTabId, trimmed);
        setEditingTabId(null);
        setEditingLabel('');
    };

    const handleCancelRename = () => {
        setEditingTabId(null);
        setEditingLabel('');
    };

    //  Delete Handler 

    const handleRequestDelete = (tab: DashboardTabConfig) => {
        handleCloseMenu();
        const isConfirmed = window.confirm(
            `Are you sure you want to delete "${tab.label}"? All card configurations for this tab will be lost.`
        );
        if (isConfirmed) onDeleteTab(tab.id);
    };

    // Color Handlers

    const handleOpenColorPicker = (tabId: TabId) => {
        handleCloseMenu();
        setColorPickerTabId(tabId);
    };

    const handleSelectColor = (tabId: TabId, colorKey: TabColorKey) => {
            onSetTabColor(tabId, colorKey);
            setColorPickerTabId(null);
        };

    const handleCloseColorPicker = () => {
        setColorPickerTabId(null);
    };

    return (
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {tabs.length} / {MAX_TABS} tabs
            </Typography>

            <Stack direction="column" spacing="sm">
                {tabs.map((tab) => {
                    const isEditing = editingTabId === tab.id;
                    const isColorPickerOpen = colorPickerTabId === tab.id;
                    const enabledCount = tab.cards.filter((c) => c.enabled).length;
                    const currentColorKey = tab.colorKey;
                    const tabColor = theme.palette.dashboardTabs[currentColorKey];
                    return (
                        <Box
                            key={tab.id}
                            sx={{
                                p: 2,
                                borderRadius: 1,
                                border: 1,
                                borderColor: tab.id === activeTabId ? 'primary.main' : 'divider',
                                bgcolor: 'transparent',
                                boxShadow: tab.id === activeTabId
                                    ? `0 0 12px 2px ${tabColor}80`
                                    : 'none',
                            }}
                        >
                            {isEditing ? (
                                <Stack direction="row" spacing="sm" alignItems="center">
                                    <TextField
                                        size="small"
                                        value={editingLabel}
                                        onChange={(e) => setEditingLabel(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') handleConfirmRename();
                                            if (e.key === 'Escape') handleCancelRename();
                                        }}
                                        autoFocus
                                        fullWidth
                                        inputProps={{ maxLength: 30 }}
                                    />
                                    <IconButton
                                        size="small"
                                        color="success"
                                        onClick={handleConfirmRename}
                                        disabled={!editingLabel.trim()}
                                        aria-label="Confirm rename"
                                    >
                                        <CheckCircleOutlineIcon />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={handleCancelRename}
                                        aria-label="Cancel rename"
                                    >
                                        <CancelOutlinedIcon />
                                    </IconButton>
                                </Stack>
                            ) : (
                                <>
                                    <Stack direction="row" alignItems="center" spacing="sm">
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Typography variant="subtitle1" fontWeight="medium">
                                                {tab.label}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {enabledCount} {enabledCount === 1 ? 'card' : 'cards'} active
                                            </Typography>
                                        </Box>

                                        {tab.id === activeTabId && (
                                            <MuiChip
                                                label="Active"
                                                size="small"
                                                variant="filled"
                                                sx={{
                                                    bgcolor: (theme) => theme.palette.primary.light,
                                                    color: (theme) => theme.palette.primary.contrastText,
                                                }}
                                            />                                           
                                        )}

                                        {!tab.isDefault && (
                                            <IconButton
                                                size="small"
                                                onClick={(e) => handleOpenMenu(e, tab.id)}
                                                aria-label={`Options for ${tab.label}`}
                                            >
                                                <ArrowDropDownCircleSharpIcon fontSize="small" />
                                            </IconButton>
                                        )}
                                    </Stack>

                                    {isColorPickerOpen && (
                                        <Stack
                                            direction="row"
                                            spacing="xs"
                                            alignItems="center"
                                            sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}
                                        >
                                            {TAB_PALETTE_KEYS.map((key) => {
                                                const pickerColor = theme.palette.dashboardTabs[key];
                                                return (
                                                    <Box
                                                        key={key}
                                                        onClick={() => handleSelectColor(tab.id, key)}
                                                        sx={{
                                                            width: 28,
                                                            height: 28,
                                                            borderRadius: '50%',
                                                            backgroundColor: pickerColor,
                                                            cursor: 'pointer',
                                                            border: '2px solid',
                                                            borderColor: key === currentColorKey
                                                                ? 'text.primary'
                                                                : 'transparent',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            transition: 'transform 0.15s ease-in-out',
                                                            '&:hover': {
                                                                transform: 'scale(1.15)',
                                                            },
                                                        }}
                                                        role="radio"
                                                        aria-checked={key === currentColorKey}
                                                        aria-label={`Select color ${key}`}
                                                        tabIndex={0}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                handleSelectColor(tab.id, key);
                                                            }
                                                        }}
                                                    >
                                                        {key === currentColorKey && (
                                                            <CheckIcon
                                                                sx={{
                                                                    fontSize: 16,
                                                                    color: '#fff',
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                );
                                            })}
                                            <IconButton
                                                size="small"
                                                onClick={handleCloseColorPicker}
                                                aria-label="Close color picker"
                                                sx={{ ml: 0.5 }}
                                            >
                                                <CancelOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    )}
                                </>
                            )}
                        </Box>
                    );
                })}

                {/* Add Tab Row */}
                {isAddingTab ? (
                    <Box sx={{ p: 2, borderRadius: 1, border: 1, borderColor: 'divider' }}>
                        <Stack direction="row" spacing="sm" alignItems="center">
                            <TextField
                                size="small"
                                placeholder="New tab name"
                                value={newTabLabel}
                                onChange={(e) => setNewTabLabel(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleConfirmAddTab();
                                    if (e.key === 'Escape') handleCancelAddTab();
                                }}
                                autoFocus
                                fullWidth
                                inputProps={{ maxLength: 30 }}
                            />
                            <IconButton
                                size="small"
                                color="success"
                                onClick={handleConfirmAddTab}
                                disabled={!newTabLabel.trim()}
                                aria-label="Confirm add tab"
                            >
                                <CheckCircleOutlineIcon />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={handleCancelAddTab}
                                aria-label="Cancel add tab"
                            >
                                <CancelOutlinedIcon />
                            </IconButton>
                        </Stack>
                    </Box>
                ) : (
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleStartAddTab}
                        disabled={isAtTabLimit}
                        fullWidth
                        sx={{ mt: 1 }}
                    >
                        {isAtTabLimit ? `Tab limit reached (${MAX_TABS})` : 'Add Tab'}
                    </Button>
                )}
            </Stack>

            {/* Shared dropdown menu for all non-default tabs */}
            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleCloseMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem
                    onClick={() => {
                        const tab = tabs.find((t) => t.id === menuTabId);
                        if (tab) handleStartRename(tab);
                    }}
                >
                    <ListItemIcon>
                        <EditSharpIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Rename</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        if (menuTabId) handleOpenColorPicker(menuTabId);
                    }}
                >
                    <ListItemIcon>
                        <ColorLensSharpIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Change Color</ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        const tab = tabs.find((t) => t.id === menuTabId);
                        if (tab) handleRequestDelete(tab);
                    }}
                    sx={{ color: 'error.main' }}
                >
                    <ListItemIcon>
                        <DeleteOutlineSharpIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <ListItemText>Delete</ListItemText>
                </MenuItem>
            </Menu>
        </Box>
    );
};
